<template>
  <div class="watermark-container" :style="containerStyle">
    <div
      v-if="showWatermark"
      class="watermark"
      :style="watermarkStyle"
    ></div>
    <slot></slot>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = withDefaults(defineProps<{
  zIndex?: number
  text?: string
  rotate?: number
  color?: string
  fontSize?: number
  gapX?: number
  gapY?: number
  show?: boolean
}>(), {
  zIndex: 1000,
  text: '',
  rotate: -30,
  color: 'rgba(180, 180, 180, 0.3)',
  fontSize: 16,
  gapX: 20,
  gapY: 20,
  show: true
})

const showWatermark = computed(() => props.show && props.text)

const containerStyle = computed(() => ({
  position: 'absolute' as const,
  overflow: 'hidden'
}))

/** 用 canvas 测量文字宽度，回退到估算值 */
function measureTextWidth(text: string, fontSize: number): number {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (ctx) {
      ctx.font = `${fontSize}px sans-serif`
      return ctx.measureText(text).width
    }
  } catch (_) {}
  return text.length * fontSize * 0.6
}

const watermarkStyle = computed(() => {
  const rad = (props.rotate * Math.PI) / 180
  const absRad = Math.abs(rad)
  const textW = measureTextWidth(props.text ?? '', props.fontSize)
  const textH = props.fontSize

  // 旋转后包围盒尺寸（sin/cos 取绝对值，与旋转方向无关）
  const bboxW = Math.ceil(textW * Math.abs(Math.cos(absRad)) + textH * Math.abs(Math.sin(absRad)))
  const bboxH = Math.ceil(textW * Math.abs(Math.sin(absRad)) + textH * Math.abs(Math.cos(absRad)))

  // tile 尺寸 = 包围盒 + gap（允许重叠，gap 可以为 0 或负数）
  const tileW = bboxW + props.gapX
  const tileH = bboxH + props.gapY

  const cx = tileW / 2
  const cy = tileH / 2

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${tileW}" height="${tileH}">
  <text
    x="${cx}"
    y="${cy}"
    fill="${props.color}"
    font-size="${props.fontSize}"
    font-family="sans-serif"
    text-anchor="middle"
    dominant-baseline="middle"
    transform="rotate(${props.rotate}, ${cx}, ${cy})"
  >${props.text}</text>
</svg>`

  const base64 = btoa(unescape(encodeURIComponent(svg)))

  return {
    position: 'absolute' as const,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none' as const,
    backgroundImage: `url("data:image/svg+xml;base64,${base64}")`,
    backgroundSize: `${tileW}px ${tileH}px`,
    zIndex: props.zIndex
  }
})
</script>

<style scoped>
.watermark-container {
  width: 100%;
  height: 100%;
}
</style>
